import React, { FC, useCallback, useMemo } from 'react';
import { Formik, validateYupSchema, yupToFormErrors } from 'formik';
import * as Yup from 'yup';
import { LoadingPage, useToast } from 'Shared/components';
import { isMissing, isPresent } from 'Shared/utils';

import {
  DriversList,
  Footer,
  ScreenHeader,
  RoutePrompt,
  FooterNextButton,
  Body,
} from 'QuoteApp/components';
import { transformSaveDriversRequest } from 'QuoteApp/services/endpoints/PostSaveDrivers';
import {
  getDriverDataByFieldValue,
  getDriverDataCompleteness,
  driverValidation,
  formatDriverDisplay,
  getErrorMessage,
} from 'QuoteApp/utils';
import { useFooter, useQuoteAppFeatureFlags } from 'QuoteApp/utils/hooks';
import { EditProvider, FieldDataContext } from 'QuoteApp/utils/contexts';
import { useAppContext } from 'QuoteApp/services/providers';
import { RewardsBanner } from 'QuoteApp/components/RewardsBanner';

export const Drivers: FC = () => {
  const { nextStep } = useFooter(0);
  const { toast } = useToast();
  const {
    details,
    setDetails,
    rate,
    refreshRate,
    config,
    refreshConfig,
    api,
    rewardsEnrollmentResponse,
  } = useAppContext();

  const {
    selfReportedAccidentsForClassificationsFlag,
    collectDayOfAccidentFlag,
  } = useQuoteAppFeatureFlags();

  const driversData = useMemo(
    () => getDriverDataByFieldValue(details.drivers, 'value'),
    [details]
  );

  const driverServerErrors = useMemo(
    () => getDriverDataByFieldValue(details.drivers, 'errors'),
    [details.drivers]
  );

  const driverDataFieldsEditable = useMemo(
    () => getDriverDataByFieldValue(details.drivers, 'editable'),
    [details.drivers]
  );

  const driverDataFieldsMasked = useMemo(
    () => getDriverDataByFieldValue(details.drivers, 'masked'),
    [details.drivers]
  );

  const driversDataCompleteness = useMemo(
    () => getDriverDataCompleteness(driverServerErrors),
    [driverServerErrors]
  );

  // used for save modals to display the name of the entity in edit mode
  const formatEditingId = useCallback(
    (editingId: string | null) => {
      if (isMissing(editingId) || isMissing(config)) {
        return '';
      } else if (isPresent(driversData[editingId])) {
        return formatDriverDisplay(driversData[editingId], config);
      } else {
        // this should never happen?
        return '';
      }
    },
    [config, driversData]
  );

  if (!config) {
    return <LoadingPage />;
  }

  return (
    <>
      <Body data-testid="drivers-page">
        {rewardsEnrollmentResponse?.isEnrolled && <RewardsBanner />}
        <ScreenHeader
          headerText="Drivers"
          subHeaderText="All drivers in the household must be either added to the policy,
        or explicitly excluded."
        />
        <FieldDataContext.Provider
          value={{
            serverErrors: { drivers: driverServerErrors },
            editableFields: { drivers: driverDataFieldsEditable },
            alwaysShowErrors: true,
          }}
        >
          <EditProvider>
            {({ editingId, changeEditingId }) => {
              return (
                <Formik
                  enableReinitialize
                  initialValues={{ drivers: driversData }}
                  validate={(values) => {
                    if (!isPresent(editingId)) {
                      throw new Error('failed to edit');
                    }
                    try {
                      validateYupSchema(
                        values,
                        Yup.object().shape({
                          drivers: Yup.object().shape({
                            [editingId]: driverValidation(
                              values.drivers[editingId],
                              selfReportedAccidentsForClassificationsFlag,
                              collectDayOfAccidentFlag
                            ),
                          }),
                        }),
                        true,
                        values
                      );
                      return {};
                    } catch (err) {
                      return yupToFormErrors(err);
                    }
                  }}
                  onSubmit={async (
                    values,
                    { setSubmitting, setStatus, setFieldValue }
                  ) => {
                    try {
                      if (!isPresent(editingId)) {
                        throw new Error('Failed to edit');
                      }

                      const response = await api.saveAppData(
                        {
                          data: {
                            values: values.drivers,
                            initialValues: driversData,
                            editableFields: driverDataFieldsEditable,
                            maskedFields: driverDataFieldsMasked,
                          },
                          transformRequest: transformSaveDriversRequest,
                        },
                        { suppressDefaultToastError: true }
                      );

                      await refreshConfig();
                      rate && refreshRate();
                      setDetails(response.data);

                      // Syncing "licenseNumber" manually — server may mask the value (e.g. sent "A9876543", received "A987****")
                      setFieldValue(
                        `drivers.${editingId}.licenseNumber`,
                        response.data.drivers?.[editingId]?.licenseNumber?.value
                      );

                      changeEditingId(null);
                      // IncidentList/IncidentForm uses status to store temporary details data; clear on successful save
                      setStatus(undefined);
                    } catch (e) {
                      toast({
                        type: 'error',
                        message: getErrorMessage(e),
                        withSecondaryNav: false,
                        icon: 'error',
                      });
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                >
                  {({ dirty, isValid, isSubmitting }) => (
                    <>
                      <RoutePrompt
                        when={dirty && !isSubmitting}
                        editingIdDisplay={formatEditingId(editingId)}
                        isValid={isValid}
                      />
                      <DriversList
                        editingIdDisplay={formatEditingId(editingId)} // used to display when form is dirty and modal pops up
                        dataCompleteness={driversDataCompleteness}
                        onDriversPage
                      />
                    </>
                  )}
                </Formik>
              );
            }}
          </EditProvider>
        </FieldDataContext.Provider>
      </Body>
      <Footer
        nextButton={
          <FooterNextButton onClick={() => nextStep.onClickSuccess()}>
            {nextStep.text}
          </FooterNextButton>
        }
      />
    </>
  );
};
